import React, { Component } from 'react';
import { List, Paper } from '@mui/material';

import { SanitizedDocument } from '../../store/documents';
import { hasAll, hasOnlySome, toggle, toggleManyTogether } from './arrayUtilities';
import DocumentHierarchy, { DocumentCategory } from './DocumentHierarchy';
import TreeItem from './TreeItem';

interface Props {
  readonly metaPdfs: readonly SanitizedDocument[];
  readonly components: readonly SanitizedDocument[];
  readonly selectedPages: readonly number[];
  readonly onSelectedPagesChange: (selectedPages: readonly number[]) => void;
}

interface State {
  readonly expandedItems: ReadonlyArray<DocumentCategory | number>;
}

export default class MaterialDocumentTree extends Component<Props, State> {
  state: State = {
    expandedItems: ['components'],
  };

  get documents() {
    return new DocumentHierarchy(this.props.components, this.props.metaPdfs);
  }

  handleToggleItemExpansion = (key: DocumentCategory | number) => {
    this.setState((prevState) => ({ expandedItems: toggle(prevState.expandedItems, key) }));
  };

  handleCategoryClick = (category: DocumentCategory) => {
    this.props.onSelectedPagesChange(
      toggleManyTogether(this.props.selectedPages, this.documents.getPagesInCategory(category)),
    );
  };

  handlePdfClick = (category: DocumentCategory, pdfId: number) => {
    this.props.onSelectedPagesChange(
      toggleManyTogether(this.props.selectedPages, this.documents.getPagesInDocument(pdfId)),
    );
  };

  handlePageClick = (topLevelCategory: DocumentCategory, pdfId: number, pdfPageId: number) => {
    this.props.onSelectedPagesChange(toggle(this.props.selectedPages, pdfPageId));
  };

  isCategorySelectionIndeterminate(category: DocumentCategory) {
    return hasOnlySome(this.props.selectedPages, this.documents.getPagesInCategory(category));
  }

  isDocumentSelectionIndeterminate(topLevelCategory: DocumentCategory, pdfId: number) {
    return hasOnlySome(this.props.selectedPages, this.documents.getPagesInDocument(pdfId));
  }

  isCategorySelected(category: DocumentCategory) {
    const pagesInCategory = this.documents.getPagesInCategory(category);
    return pagesInCategory.length > 0 && hasAll(this.props.selectedPages, pagesInCategory);
  }

  isDocumentSelected(documentId: number) {
    return hasAll(this.props.selectedPages, this.documents.getPagesInDocument(documentId));
  }

  renderPages(pages: readonly number[], category: DocumentCategory, documentId: number) {
    let pageNumber = 1;

    return pages.map((pageId) => (
      <TreeItem
        key={pageId}
        text={`Page ${pageNumber++}`}
        sx={{ pl: 5 }}
        expanded={this.state.expandedItems.includes(pageId)}
        checked={this.props.selectedPages.includes(pageId)}
        onClick={() => this.handlePageClick(category, documentId, pageId)}
      />
    ));
  }

  render() {
    const things = Object.entries(this.documents) as readonly [DocumentCategory, Map<number, number[]>][];

    const listItems = things.map(([category, documents]) => (
      <TreeItem
        key={category}
        text={category === 'components' ? 'Components' : 'Meta PDFs'}
        disabled={documents.size === 0}
        expanded={this.state.expandedItems.includes(category)}
        checked={this.isCategorySelected(category)}
        indeterminate={this.isCategorySelectionIndeterminate(category)}
        onClick={() => this.handleCategoryClick(category)}
        onExpanded={() => this.handleToggleItemExpansion(category)}
      >
        {Array.from(documents).map(([documentId, pages]) => (
          <TreeItem
            key={documentId}
            text={this.documents.getName(documentId)}
            sx={{ pl: 4 }}
            expanded={this.state.expandedItems.includes(documentId)}
            checked={this.isDocumentSelected(documentId)}
            indeterminate={this.isDocumentSelectionIndeterminate(category, documentId)}
            onClick={() => this.handlePdfClick(category, documentId)}
            onExpanded={() => this.handleToggleItemExpansion(documentId)}
          >
            {this.renderPages(pages, category, documentId)}
          </TreeItem>
        ))}
      </TreeItem>
    ));

    return (
      <Paper sx={{ bgcolor: 'backContrast.main' }} elevation={2}>
        <div>
          <List
            sx={{
              maxHeight: 400,
              overflowY: 'hidden',
              '&:hover': {
                overflowY: 'auto',
              },
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
            }}
          >
            <div className="listItem">{listItems}</div>
          </List>
        </div>
      </Paper>
    );
  }
}
